/**
 * MessageEnvelopeFactory
 *
 * Concrete implementation of IMessageTransformer
 * Converts raw SignalR messages to IEnvelope
 *
 * Single Responsibility: Message transformation and validation
 */

import type { IMessageTransformer, TransformOptions, TransformationDetails, TransformationError } from '../interfaces';
import type { IEnvelope } from '../../types';

export class MessageEnvelopeFactory implements IMessageTransformer {
  private transformStats = {
    total: 0,
    success: 0,
    errors: 0,
    totalDurationMs: 0,
    lastError: undefined as TransformationError | undefined,
  };

  transform(rawMessage: unknown, options: TransformOptions): TransformationDetails {
    const startTime = Date.now();

    try {
      // Validate raw message
      const validationErrors = this.validate(rawMessage);
      if (validationErrors.length > 0) {
        const duration = Date.now() - startTime;
        this.recordError();
        return {
          success: false,
          errors: validationErrors,
          duration,
          timestamp: new Date(),
          inputShape: this.analyzeShape(rawMessage),
        };
      }

      // Extract metadata
      const metadata = this.extractMetadata(rawMessage, options.eventName);

      // Create envelope
      const envelope = this.createEnvelope(rawMessage, {
        ...options,
        metadata: { ...metadata, ...options.metadata },
      });

      const duration = Date.now() - startTime;
      this.recordSuccess(duration);

      return {
        success: true,
        envelope,
        duration,
        timestamp: new Date(),
        inputShape: this.analyzeShape(rawMessage),
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      const transformError: TransformationError = {
        code: 'TRANSFORM_ERROR',
        message: error instanceof Error ? error.message : String(error),
      };

      this.recordError();
      this.transformStats.lastError = transformError;

      return {
        success: false,
        errors: [transformError],
        duration,
        timestamp: new Date(),
      };
    }
  }

  validate(rawMessage: unknown, expectedShape?: Record<string, unknown>): TransformationError[] {
    const errors: TransformationError[] = [];

    // Check for null/undefined
    if (rawMessage === null || rawMessage === undefined) {
      errors.push({
        code: 'NULL_MESSAGE',
        message: 'Message is null or undefined',
      });
      return errors;
    }

    // Check for object
    if (typeof rawMessage !== 'object') {
      errors.push({
        code: 'INVALID_TYPE',
        message: `Expected object, got ${typeof rawMessage}`,
        value: rawMessage,
      });
      return errors;
    }

    // If expectedShape provided, validate against it
    if (expectedShape) {
      for (const [key, expectedType] of Object.entries(expectedShape)) {
        const msg = rawMessage as Record<string, unknown>;
        const actualType = typeof msg[key];

        if (actualType !== expectedType && msg[key] !== undefined) {
          errors.push({
            code: 'TYPE_MISMATCH',
            message: `Field '${key}': expected ${expectedType}, got ${actualType}`,
            field: key,
            value: msg[key],
          });
        }
      }
    }

    return errors;
  }

  extractMetadata(rawMessage: unknown, eventName: string): Partial<Record<string, unknown>> {
    if (typeof rawMessage !== 'object' || rawMessage === null) {
      return { eventName };
    }

    const msg = rawMessage as Record<string, unknown>;

    return {
      eventName,
      author: msg.author,
      timestamp: msg.timestamp || new Date().toISOString(),
      messageId: msg.messageId,
      userId: msg.userId,
      topic: msg.topic,
      serverId: msg.serverId,
      groupName: msg.groupName,
    };
  }

  createFallbackEnvelope(
    rawMessage: unknown,
    options: Pick<TransformOptions, 'serverId' | 'eventName'>
  ): IEnvelope {
    const messageId = `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const now = new Date();

    return {
      meta: {
        id: messageId,
        serverId: options.serverId,
        transport: 'signalr',
        protocol: 'json',
        receivedAt: now,
        topic: `fallback:${options.eventName}`,
        type: options.eventName,
      },
      direction: 'incoming' as const,
      raw: JSON.stringify(rawMessage || {}),
      body: {
        data: {
          metadata: {
            source: 'signalr-hub',
            receivedAt: now.toISOString(),
            messageType: options.eventName,
            isFallback: true,
          },
          data: rawMessage || {},
        },
      },
      headers: {
        'content-type': 'application/json',
        'x-transport': 'signalr',
        'x-event-type': options.eventName,
        'x-fallback': 'true',
      },
    };
  }

  getStatistics() {
    const average =
      this.transformStats.success > 0
        ? this.transformStats.totalDurationMs / this.transformStats.success
        : 0;

    return {
      totalTransformations: this.transformStats.total,
      successCount: this.transformStats.success,
      errorCount: this.transformStats.errors,
      averageTimeMs: Math.round(average * 100) / 100,
      lastError: this.transformStats.lastError,
    };
  }

  // Private methods

  private createEnvelope(
    rawMessage: unknown,
    options: TransformOptions & { metadata: Record<string, unknown> }
  ): IEnvelope {
    const messageId = options.messageId || `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const now = new Date();

    return {
      meta: {
        id: messageId,
        serverId: options.serverId,
        transport: 'signalr',
        protocol: 'json',
        receivedAt: now,
        topic: options.topic,
        type: options.messageType,
      },
      direction: 'incoming' as const,
      raw: JSON.stringify(rawMessage),
      body: {
        data: {
          metadata: {
            source: 'signalr-hub',
            receivedAt: now.toISOString(),
            messageType: options.messageType,
            eventName: options.eventName,
            ...options.metadata,
          },
          data: rawMessage,
        },
      },
      headers: {
        'content-type': 'application/json',
        'x-transport': 'signalr',
        'x-event-type': options.messageType,
      },
    };
  }

  private analyzeShape(input: unknown): { type: string; keys: string[]; hasNullValues: boolean } {
    if (input === null) {
      return { type: 'null', keys: [], hasNullValues: true };
    }

    if (typeof input !== 'object') {
      return { type: typeof input, keys: [], hasNullValues: false };
    }

    if (Array.isArray(input)) {
      return { type: 'array', keys: [], hasNullValues: input.includes(null) };
    }

    const keys = Object.keys(input);
    const hasNullValues = Object.values(input).some((v) => v === null);

    return {
      type: 'object',
      keys,
      hasNullValues,
    };
  }

  private recordSuccess(duration: number): void {
    this.transformStats.total++;
    this.transformStats.success++;
    this.transformStats.totalDurationMs += duration;
  }

  private recordError(): void {
    this.transformStats.total++;
    this.transformStats.errors++;
  }
}
